import React, { Fragment, useEffect, useState } from "react";
import PageHeader from "../../common/components/PageHeader";
import { Box, Divider, Grid, LinearProgress, linearProgressClasses, Typography } from "@mui/material";
import CommonCard from "../../common/components/CommonCard";
import styled from "@emotion/styled";
import CommonButton from "../../common/components/CommonButton";
import { removeEmptyFields, validateEmail } from "../../common/const";
import StudentDetails from "./StudentDetail";
import ProjectBackground from "./ProjectBackground";
import Skills from "./Skill";
import TimeLine from "./Timeline";
import CareerAlignment from "./CareerAlignment";
import AcademicBackground from "./AcademicBackground";
import ProjectGoal from "./ProjectGoal";
import LearningStyle from "./LearningStyle";
import AcknowledgeMent from "./AcknowledgeMent";
import Schedule from "./Schdule";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { setLoading } from "../../features/authSlice";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import { saveIntakeForm } from "../../api/projectApi";

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 6,
    borderRadius: 5,
    marginTop: 8,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: theme.palette.grey[200],
        ...theme.applyStyles('dark', {
            backgroundColor: theme.palette.grey[800],
        }),
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor: '#10AE2D',
        ...theme.applyStyles('dark', {
            backgroundColor: '#10AE2D',
        }),
    },
}));

const checkSectionComplete = (sectionFields, formData) => {
    return sectionFields.every(field => {
        const value = formData[field];
        return value !== undefined && value !== null && value.toString().trim() !== '';
    });
};

const ProjectIntakeForm = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user, loading } = useSelector((state) => state.auth);
    const [progress, setProgress] = useState(0);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        schoolName: '',
        grade: '',
        parentName: '',
        parentEmail: '',
        timezone: '',
        major: '',
        workedBefore: 'Yes',
        projectDescription: '',
        areaOfInterest: '',
        skills: '',
        platforms: '',
        experienceLevel: 'Beginner',
        preferredDuration: '',
        weeklyTime: '',
        targetDate: '',
        collegeProjectSupport: '',
        careerPathInterest: '',
        gpa: 1,
        academicSubject: '',
        needingSupport: '',
        currentAP: '',
        primaryGoal: '',
        targetCollege: '',
        intendedOutcome: '',
        learningStyle: '',
        learningNeed: '',
        days: '',
        time: '',
        frequency: '',
        duration: '',
        additionalNotes: '',
        dates: ''
    });

    const validateField = (name, value) => {
        switch (name) {
            case 'firstName':
                if (!value) return 'First Name is required';
                return '';
            case 'lastName':
                if (!value) return 'Last Name is required';
                return '';
            case 'schoolName':
                if (!value) return 'School Name is required';
                return '';
            case 'grade':
                if (!value) return 'Grade is required';
                return '';
            case 'parentName':
                if (!value) return 'Parent Name is required';
                return '';
            case 'parentEmail':
                if (!value) return 'Parent Email is required';
                return '';
            case 'email':
                if (!value) return 'Email is required';
                if (!validateEmail(value)) return 'Please enter a valid email address';
                return '';
            case 'projectDescription':
                if (!value && formData.workedBefore === 'Yes') return 'Project List is required';
                return '';
            default:
                return '';
        }
    };

    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach(key => {
            const error = validateField(key, formData[key]);
            if (error) newErrors[key] = error;
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (errors[name]) {
            const error = validateField(name, value);
            if (!error) {
                setErrors(prev => ({ ...prev, [name]: undefined }));
            } else {
                setErrors(prev => ({ ...prev, [name]: error }));
            }
        }
    };

    const sectionFields = {
        studentDetails: ['firstName', 'lastName', 'email', 'schoolName', 'grade', 'parentName', 'parentEmail', 'timezone', 'major'],
        projectBackground: ['workedBefore', 'projectDescription', 'areaOfInterest'],
        skills: ['skills', 'platforms', 'experienceLevel'],
        timeline: ['preferredDuration', 'weeklyTime', 'targetDate'],
        careerAlignment: ['collegeProjectSupport', 'careerPathInterest'],
        academicBackground: ['gpa', 'academicSubject', 'needingSupport', 'currentAP'],
        projectGoal: ['primaryGoal', 'targetCollege', 'intendedOutcome'],
        learningStyle: ['learningStyle', 'learningNeed'],
        schedule: ['days', 'time', 'frequency', 'duration'],
        acknowledgement: ['additionalNotes', 'dates']
    };

    // Calculate progress whenever formData changes
    useEffect(() => {
        calculateProgress();
    }, [formData]);

    const calculateProgress = () => {
        let completedSections = 0;
        const totalSections = Object.keys(sectionFields).length;

        // Check each section
        Object.values(sectionFields).forEach(fields => {
            if (checkSectionComplete(fields, formData)) {
                completedSections++;
            }
        });

        // Calculate percentage (10% per section since there are 10 sections)
        const newProgress = Math.round((completedSections / totalSections) * 100);
        setProgress(newProgress);
    };

    const handleSubmit = async () => {
        try {
            if (!validateForm()) return;
            dispatch(setLoading({
                loading: true
            }));
            const data = {
                "student_id": user?.student_id,
                "first_name": formData?.firstName,
                "last_name": formData?.lastName,
                "school_email": formData?.email,
                "school_name": formData?.schoolName,
                "grade": formData?.grade,
                "parent_name": formData?.parentName,
                "parent_email": formData?.parentEmail,
                "timezone": formData?.timezone,
                "major": formData?.major,
                "current_gpa": formData?.gpa,
                "strongest_subjects": formData?.academicSubject,
                "subjects_needing_support": formData?.needingSupport,
                "ap_courses": formData?.currentAP,
                "worked_on_project_before": formData?.workedBefore?.toUpperCase(),
                "previous_project_details": formData?.projectDescription,
                "area_of_interest": formData?.areaOfInterest,
                "primary_goal": formData?.primaryGoal,
                "target_colleges": formData?.targetCollege,
                "intended_outcome": formData?.intendedOutcome,
                "relevant_skills": formData?.skills,
                "tools_known": formData?.platforms,
                "experience_level": formData?.experienceLevel?.toUpperCase(),
                "learning_style": formData?.learningStyle,
                "accommodations_needed": formData?.learningNeed,
                "preferred_days": formData?.days,
                "preferred_time": formData?.time,
                "frequency": formData?.frequency,
                "session_duration": formData?.duration,
                "preferred_duration": formData?.preferredDuration,
                "weekly_hours": formData?.weeklyTime,
                "target_completion_date": formData?.targetDate !== '' ? new Date(formData?.targetDate)?.toISOString() : '',
                "college_support_description": formData?.collegeProjectSupport,
                "career_interest": formData?.careerPathInterest,
                "additional_notes": formData?.additionalNotes,
                "acknowledgement_date": formData?.dates !== '' ? new Date(formData?.dates).toISOString() : ''
            };
            const response = await saveIntakeForm(removeEmptyFields(data));
            if (response.success) {
                setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    schoolName: '',
                    grade: '',
                    parentName: '',
                    parentEmail: '',
                    timezone: '',
                    major: '',
                    workedBefore: 'Yes',
                    projectDescription: '',
                    areaOfInterest: '',
                    skills: '',
                    platforms: '',
                    experienceLevel: 'Beginner',
                    preferredDuration: '',
                    weeklyTime: '',
                    targetDate: '',
                    collegeProjectSupport: '',
                    careerPathInterest: '',
                    gpa: '',
                    academicSubject: '',
                    needingSupport: '',
                    currentAP: '',
                    primaryGoal: '',
                    targetCollege: '',
                    intendedOutcome: '',
                    learningStyle: '',
                    learningNeed: '',
                    days: '',
                    time: '',
                    frequency: '',
                    duration: '',
                    additionalNotes: '',
                    dates: ''
                });
                showNotification('Project intake form submitted successfully', 'success');
                navigate('/build-project');
            }
        } catch (err) {
            console.log(err);
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }


    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <PageHeader
                title="Build Project Intake Form"
                content={
                    <Typography color="text.secondary" sx={{ fontSize: 14, }}>
                        Please complete the following details to help us match you with the ideal project opportunities.
                    </Typography>
                }
            />
            <CommonCard
                paddingBottom="0px"
                borderRadius={2}
                content={
                    <Fragment>
                        <Typography fontWeight={600} textAlign="right">{`${progress}% Complete`}</Typography>
                        <BorderLinearProgress variant="determinate" value={progress} />
                    </Fragment>
                }
            />
            <Grid container spacing={2} mb={4}>
                <Grid item md={6}>
                    <StudentDetails
                        formData={formData}
                        handleInputChange={handleInputChange}
                        errors={errors}
                    />
                    <ProjectBackground
                        formData={formData}
                        handleInputChange={handleInputChange}
                        errors={errors}
                        setFormData={setFormData}
                    />
                    <Skills
                        formData={formData}
                        handleInputChange={handleInputChange}
                        errors={errors}
                        setFormData={setFormData}
                    />
                    <TimeLine
                        formData={formData}
                        handleInputChange={handleInputChange}
                        errors={errors}
                    />
                    <CareerAlignment
                        formData={formData}
                        handleInputChange={handleInputChange}
                        errors={errors}
                    />
                </Grid>
                <Grid item md={6}>
                    <AcademicBackground
                        formData={formData}
                        handleInputChange={handleInputChange}
                        errors={errors}
                    />
                    <ProjectGoal
                        formData={formData}
                        handleInputChange={handleInputChange}
                        errors={errors}
                    />
                    <LearningStyle
                        formData={formData}
                        handleInputChange={handleInputChange}
                        errors={errors}
                    />
                    <Schedule
                        formData={formData}
                        handleInputChange={handleInputChange}
                        errors={errors}
                    />
                    <AcknowledgeMent
                        formData={formData}
                        handleInputChange={handleInputChange}
                        errors={errors}
                    />
                </Grid>
            </Grid>
            <Divider />
            <Box display="flex" justifyContent="end" gap={1} mt={2}>
                <CommonButton
                    label="Cancel"
                    marginBottom="0px"
                    color="black"
                    background="White"
                    border={`1px solid #0000001A`}
                    width="200px"
                    handleClick={() => setFormData({
                        firstName: '',
                        lastName: '',
                        email: '',
                        schoolName: '',
                        grade: '',
                        parentName: '',
                        parentEmail: '',
                        timezone: '',
                        major: '',
                        workedBefore: 'Yes',
                        projectDescription: '',
                        areaOfInterest: '',
                        skills: '',
                        platforms: '',
                        experienceLevel: 'Beginner',
                        preferredDuration: '',
                        weeklyTime: '',
                        targetDate: '',
                        collegeProjectSupport: '',
                        careerPathInterest: '',
                        gpa: '',
                        academicSubject: '',
                        needingSupport: '',
                        currentAP: '',
                        primaryGoal: '',
                        targetCollege: '',
                        intendedOutcome: '',
                        learningStyle: '',
                        learningNeed: '',
                        days: '',
                        time: '',
                        frequency: '',
                        duration: '',
                        additionalNotes: '',
                        dates: ''
                    })}
                />
                <CommonButton
                    label="Create"
                    marginBottom="0px"
                    color="black"
                    width="200px"
                    handleClick={() => handleSubmit()}
                />
            </Box>
        </Fragment>
    )
}

export default ProjectIntakeForm;